import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  LinearProgress,
  Tooltip,
  useTheme
} from '@mui/material';
import { FirebaseUsageData } from '../../services/firebaseUsageService';

// واجهة خصائص جدول تفاصيل الاستخدام
interface UsageDetailsTableProps {
  title: string;
  data: any[];
  columns: {
    id: string;
    label: string;
    format?: (value: any) => string;
    align?: 'left' | 'right' | 'center';
    minWidth?: number;
    renderProgress?: boolean;
    maxValue?: number;
  }[];
}

// تنسيق حجم البيانات للعرض
const formatSize = (bytes: number): string => {
  if (bytes === 0) return '0 بايت';
  
  const k = 1024;
  const sizes = ['بايت', 'كيلوبايت', 'ميجابايت', 'جيجابايت', 'تيرابايت'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// مكون جدول تفاصيل الاستخدام
const UsageDetailsTable: React.FC<UsageDetailsTableProps> = ({ title, data, columns }) => {
  const theme = useTheme();
  
  // تحديد لون شريط التقدم بناءً على النسبة
  const getProgressColor = (percentage: number) => {
    if (percentage < 50) return theme.palette.success.main;
    if (percentage < 80) return theme.palette.warning.main;
    return theme.palette.error.main;
  };
  
  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3 }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" component="div">
          {title}
        </Typography>
      </Box>
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table stickyHeader aria-label={`${title} table`}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'right'}
                  style={{ minWidth: column.minWidth || 100 }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                {columns.map((column) => {
                  const value = column.id.includes('.')
                    ? column.id.split('.').reduce((obj, key) => obj?.[key], row)
                    : row[column.id];
                  
                  return (
                    <TableCell key={column.id} align={column.align || 'right'}>
                      {column.renderProgress && column.maxValue ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ flexGrow: 1, mr: 1 }}>
                            <Tooltip 
                              title={`${Math.round((value / column.maxValue) * 100)}% من الحد المسموح`} 
                              arrow
                            >
                              <LinearProgress 
                                variant="determinate" 
                                value={Math.min(Math.round((value / column.maxValue) * 100), 100)} 
                                sx={{ 
                                  height: 8, 
                                  borderRadius: 4,
                                  backgroundColor: theme.palette.grey[200],
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: getProgressColor(Math.round((value / column.maxValue) * 100)),
                                    borderRadius: 4,
                                  }
                                }} 
                              />
                            </Tooltip>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {Math.round((value / column.maxValue) * 100)}%
                          </Typography>
                        </Box>
                      ) : column.format ? column.format(value) : value}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default UsageDetailsTable;
